import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header.jsx';
import OTPVerification from '../components/OTPVerification';
import logo from '../Images/logo rashi jyotish.png';
import api from '../services/api';

const SignIn = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1: Login Form, 2: OTP Verification
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await login(formData);
      
      // Check if OTP is required
      if (response.data && response.data.requiresOTP) {
        // Send OTP to email
        await api.post('/otp/send', {
          email: formData.email,
          purpose: 'login'
        });
        setSuccess('OTP sent to your email! Please verify to continue.');
        setStep(2);
        setLoading(false);
        return;
      }

      // Login successful without OTP
      if (response.data && response.data.token) {
        setSuccess('Login successful! Redirecting to dashboard...');
        setLoading(false);
        const redirectPath = response.data.user.role === 'admin' ? '/admin' : '/dashboard';
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const handleOTPVerified = async (otp) => {
    setLoading(true);
    setError('');

    try {
      // Login with OTP
      const response = await login({ ...formData, otp });

      if (response.data && response.data.token) {
        setSuccess('Login successful! Redirecting to dashboard...');
        const redirectPath = response.data.user.role === 'admin' ? '/admin' : '/dashboard';
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {step === 1 ? (
          // Step 1: Login Form
          <div className="card" style={{ maxWidth: '450px', width: '100%', padding: '40px' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <img src={logo} alt="SRJ Logo" style={{ width: '80px', marginBottom: '16px' }} />
              <h1 style={{ color: 'var(--maroon)', fontFamily: "'Playfair Display',serif", marginBottom: '8px' }}>
                Welcome Back
              </h1>
              <p style={{ color: 'var(--muted)' }}>Sign in to your account</p>
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '8px',
                color: '#dc2626',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#16a34a',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                ✓ {success}
              </div>
            )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label htmlFor="password" style={{ fontWeight: '600' }}>
                Password
              </label>
              <Link to="/forgot-password" style={{ color: 'var(--maroon)', fontSize: '13px', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="••••••••"
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: 'var(--accent-grad)',
              border: 'none',
              borderRadius: '10px',
              color: '#3b0b12',
              fontWeight: '800',
              fontSize: '16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--maroon)', fontWeight: '600', textDecoration: 'none' }}>
              Register here
            </Link>
          </p>
          <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '12px' }}>
            <Link to="/dashboard" style={{ color: 'var(--maroon)', textDecoration: 'none' }}>
              ← Back to Home
            </Link>
          </p>
        </div>

        <div style={{
          marginTop: '30px',
          padding: '16px',
          background: 'rgba(122,19,43,0.05)',
          borderRadius: '10px',
          fontSize: '13px',
          color: 'var(--muted)'
        }}>
        </div>
      </div>
    ) : (
      // Step 2: OTP Verification
      <OTPVerification
        email={formData.email}
        purpose="login"
        onVerified={handleOTPVerified}
        onCancel={() => setStep(1)}
        title="Verify Sign In"
        subtitle="Enter the 6-digit code sent to"
        skipVerification={true}
      />
    )}
    </div>
    </>
  );
};

export default SignIn;
